import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

import ApplicantSelect, { ApplicantOption } from './ApplicantSelect';
import { postApplicantDocumentsIdAssign } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { ApplicantDocument } from '../api/generated/model';

interface Props {
  doc: ApplicantDocument | null;
  onClose: () => void;
  onAssigned: () => void;
}

// AssignUploadModal resolves an unresolved document (needs_review): pick the
// document type, then either an existing applicant (searchable) or a new one.
const AssignUploadModal: React.FC<Props> = ({ doc, onClose, onAssigned }) => {
  const [docType, setDocType] = useState<'resume' | 'cover_letter'>('resume');
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [existing, setExisting] = useState<ApplicantOption | null>(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (doc) {
      setDocType(doc.type === 'cover_letter' ? 'cover_letter' : 'resume');
      setMode('existing');
      setExisting(null);
      setEmail('');
      setFirstName('');
      setLastName('');
      setErr(null);
    }
  }, [doc]);

  const canSubmit = mode === 'existing' ? !!existing : email.trim() !== '';

  const submit = async () => {
    if (!doc || !canSubmit) return;
    setSaving(true);
    setErr(null);
    try {
      const body: any = { doc_type: docType };
      if (mode === 'existing') {
        body.applicant_id = existing!.value;
      } else {
        body.new_applicant = {
          email: email.trim(),
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
        };
      }
      await postApplicantDocumentsIdAssign(doc.id, body);
      onAssigned();
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || 'Assign failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={!!doc} onHide={saving ? undefined : onClose} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title className="fs-6 text-truncate">Assign “{doc?.file_name}”</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger" className="py-2">{err}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label>Document type</Form.Label>
          <Form.Select value={docType} onChange={(e) => setDocType(e.target.value as 'resume' | 'cover_letter')}>
            <option value="resume">Resume</option>
            <option value="cover_letter">Cover letter</option>
          </Form.Select>
        </Form.Group>

        <div className="mb-2 d-flex gap-3">
          <Form.Check type="radio" id="assign-existing" label="Existing applicant" checked={mode === 'existing'} onChange={() => setMode('existing')} />
          <Form.Check type="radio" id="assign-new" label="New applicant" checked={mode === 'new'} onChange={() => setMode('new')} />
        </div>

        {mode === 'existing' ? (
          <ApplicantSelect value={existing} onChange={setExisting} />
        ) : (
          <>
            <Form.Group className="mb-2">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="applicant@example.com" />
            </Form.Group>
            <div className="d-flex gap-2">
              <Form.Group className="mb-2 flex-fill">
                <Form.Label>First name</Form.Label>
                <Form.Control value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-2 flex-fill">
                <Form.Label>Last name</Form.Label>
                <Form.Control value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Form.Group>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={!canSubmit || saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-1" />Assigning…</> : 'Assign'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignUploadModal;
